import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type { GtmSessionStatus } from "../api/GtmApi";
import {
  GTM_SEQUENCE_PAUSED_KEEP_STEP,
  GTM_SESSION_EXPIRED_BANNER,
  GTM_SESSION_MISSING_BANNER,
} from "../disclosureCopy";

type Props = {
  projectId: string;
  session: GtmSessionStatus | null | undefined;
  /** When true, also show a soft note if sequence is paused while connected. */
  showPausedNote?: boolean;
};

/**
 * Session failure / missing banner for Play Leads + Sequence (UX § Session failure).
 */
export default function GtmSessionBanner({ projectId, session, showPausedNote = true }: Props) {
  const { t } = useTranslation();
  const settingsHref = `/workspaces/${projectId}/gtm/leads/settings?key=Leads`;

  if (!session) return null;

  if (session.status === "rejected") {
    return (
      <div
        role="status"
        className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-950"
      >
        <span className="font-medium">{t("LinkedIn session expired")}</span>
        <span className="mt-0.5 block leading-relaxed">{t(GTM_SESSION_EXPIRED_BANNER)}</span>
        <Link className="mt-1 inline-block font-medium underline" to={settingsHref}>
          {t("Open Settings")}
        </Link>
      </div>
    );
  }

  if (session.status === "missing") {
    return (
      <div
        role="status"
        className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950"
      >
        <span className="font-medium">{t("No LinkedIn session")}</span>
        <span className="mt-0.5 block leading-relaxed">{t(GTM_SESSION_MISSING_BANNER)}</span>
        <Link className="mt-1 inline-block font-medium underline" to={settingsHref}>
          {t("Open Settings")}
        </Link>
      </div>
    );
  }

  if (showPausedNote && session.sequence === "paused") {
    return (
      <div
        role="status"
        className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800"
      >
        <span className="font-medium">{t("Sequence paused")}</span>
        <span className="mt-0.5 block leading-relaxed">{t(GTM_SEQUENCE_PAUSED_KEEP_STEP)}</span>
        <Link className="mt-1 inline-block font-medium text-blue-600 underline" to={settingsHref}>
          {t("Open Settings")}
        </Link>
      </div>
    );
  }

  return null;
}
